import { getSvg } from "./skyward.ui.svg";
import { Dictionary, ValidationMessage } from "./types";
import { ValueOf } from "./types/util/types";

type ValidationItem = {

    resultControlName: string;
    target: JQuery;
    hasTarget: boolean;
    control: JQuery;
    isVisible: boolean;
    messages: ValidationMessage[];
    isBase: boolean;
};

type ValidationType = {
    Severity: Severity;
    Color: string;
    SkipStyling: (item: ValidationItem) => boolean;
};

type ValidationMessageListItem = {
    key: string;
    value: {
        target: JQuery;
        validationList: JQuery;
    };
};

type Severity = "Error" | "Warning";

(
    function (skyward, $, undefined)
    {
        skyward.validation = skyward.validation || {};

        const classnames = Object.freeze<typeof skyward.validation.classnames>(
        {
            errorControl: "error-control",
            errorText: "error-text",
            warningText: "warning-text",
            warningControl: "warning-control",
            persist: "persist",
        });
        skyward.validation.classnames = classnames;

        const validationTypes: { [key in Severity]: ValidationType } =
        {
            Error:
            {
                Severity: "Error",
                Color: "#CC3333",
                SkipStyling: function () { return false; }
            },
            Warning:
            {
                Severity: "Warning",
                Color: "#E87107",
                SkipStyling: function (item) { return item ? true : false; }
            }
        };

        skyward.validation.clearValidationMessages = function (container, baseErrorContainer)
        {
            // TODO does not passing warninghash, etc cause any problems?
            skyward.validation.displayValidationMessages({ errors: {}, warnings: {} } as any, container, baseErrorContainer);
        };

        skyward.validation.displayValidationMessages = function (data, container, baseErrorContainer, overrideBase = false)
        {
            // Workflow.js was designed to pass in a jQuery object, so lets account for both possibilities:
            const jQContainer = (typeof container === 'string') ? $("#" + container) : container;
            const contentMessageArea = skyward.contentMessageArea.getContentMessageArea(jQContainer.parent());

            //Remove previous error/warning styling
            jQContainer.find(".objectErrors").html("");
            jQContainer.find(classnames.errorControl.dot()).removeClass(classnames.errorControl);
            jQContainer.find(classnames.errorText.dot()).not(classnames.persist.dot()).removeClass(classnames.errorText);
            jQContainer.find(classnames.warningControl.dot()).removeClass(classnames.warningControl);
            jQContainer.find(classnames.warningText.dot()).not(classnames.persist.dot()).removeClass(classnames.warningText);
            jQContainer.find(".help").empty();

            // Remove htmlViewer errors/warnings
            jQContainer.find(".js-htmlViewerWrapper").each(function ()
            {
                const wrapper = $(this);
                const control = wrapper.children().not(".js-htmlViewerOverlay");

                wrapper.replaceWith(control);
            });

            if (contentMessageArea)
            {
                contentMessageArea.resetValidation();
            }

            const validationMessageListsForTargets: ValidationMessageListItem[] = [];

            // Apply error messages
            if (data !== null && typeof data === "object")
            {
                const baseUl = $("<ul/>");

                const sumVisibleMessages = getMessageAccumulator(false);

                const { found: foundErrorCollection, notFound: notFoundErrorCollection } = processValidationCollection(data.errors);
                const { found: foundWarningCollection, notFound: notFoundWarningCollection } = processValidationCollection(data.warnings);
                const allErrors = $.extend({}, foundErrorCollection, notFoundErrorCollection);
                const allWarnings = $.extend({}, foundWarningCollection, notFoundWarningCollection);

                const iframes = skyward.changeTracking.getHtmlViewers(jQContainer);
                const promises = Promise.all(iframes.toArray().map(function (elem)
                {
                    return skyward.iframeListener.displayValidationMessages(elem, {
                        errors: mapValues(notFoundErrorCollection, elem => elem!.messages),
                        warnings: mapValues(notFoundWarningCollection, elem => elem!.messages),
                    });
                }));
                return promises.then(foundMessagesPerIframe =>
                {
                    const allFoundMessages = new Set(([] as string[]).concat(
                        Object.keys(foundWarningCollection),
                        Object.keys(foundErrorCollection),
                        ...foundMessagesPerIframe
                    ));
                    // If this is the top frame, move any errors that weren't found in iframes back to
                    // the found collection, so they can be displayed in the base validation container
                    if (window === window.parent)
                    {
                        for (const key of Object.keys(notFoundWarningCollection))
                        {
                            if (!allFoundMessages.has(key))
                            {
                                foundWarningCollection[key] = notFoundWarningCollection[key];
                            }
                        }
                        for (const key of Object.keys(notFoundErrorCollection))
                        {
                            if (!allFoundMessages.has(key))
                            {
                                foundErrorCollection[key] = notFoundErrorCollection[key];
                            }
                        }
                    }

                    let errorCount = (Object.values(allErrors) as ValidationItem[]).reduce(sumVisibleMessages, 0);
                    displayValidationListItems(foundErrorCollection, foundErrorCollection, baseUl, validationTypes.Error);

                    let warningCount = (Object.values(allWarnings) as ValidationItem[]).reduce(sumVisibleMessages, 0);
                    displayValidationListItems(foundWarningCollection, foundErrorCollection, baseUl, validationTypes.Warning);

                    // Update the warning hash for this container
                    jQContainer.data("warninghash", data.warninghash ?? null);
                    jQContainer.data("warninghashes", data.warninghashes);
                    jQContainer.data("preSaveWarningHash", data.preSaveWarningHash ?? null);

                    // Finally, insert all base error/warning messages
                    if (baseErrorContainer)
                    {
                        baseErrorContainer.html(baseUl[0]);

                        // Count for base errors/warnings that use control names other than "Base" or "base"
                        errorCount += baseErrorContainer.find(classnames.errorText.dot()).length;
                        warningCount += baseErrorContainer.find(classnames.warningText.dot()).length;
                    }

                    // Update and Display Content Message Area
                    if (contentMessageArea)
                    {
                        const sumAllMessages = getMessageAccumulator(true);
                        const totalErrors = (Object.values(allErrors) as ValidationItem[]).reduce(sumAllMessages, 0);

                        if (totalErrors > 0 && errorCount === 0)
                        {
                            // Special Validation Message
                            contentMessageArea.setAllErrorsAreHidden(true);
                        }
                        contentMessageArea.setValidationCount(errorCount, warningCount).showValidation();
                    }
                    skyward.window.notifyResize();
                    return Array.from(allFoundMessages);
                });
            }
            else
            {
                skyward.window.notifyResize();
                return Promise.resolve([]);
            }

            function processValidationCollection(validationCollection: Dictionary<string, ValidationMessage[]>)
            {
                const found: Dictionary<string, ValidationItem> = {};
                const notFound: Dictionary<string, ValidationItem> = {};
                for (const controlName in validationCollection)
                {
                    const processed = processValidationItem(controlName, validationCollection);
                    if (processed.hasTarget)
                    {
                        found[controlName] = processed;
                    }
                    else
                    {
                        notFound[controlName] = processed;
                    }
                }
                return { found, notFound };
            }

            function processValidationItem(controlName: string, validationCollection: Dictionary<string, ValidationMessage[]>)
            {
                let resultControlName = controlName;
                let resultTarget = skyward.validation.getHelpMessage(controlName, jQContainer);
                let resultControl = jQContainer.find(`[name="${resultControlName}"]`);

                if (resultTarget.length <= 0)
                {
                    const controlWithoutQualifier = controlName.split(".")[1];
                    const targetsWithoutQualifier = skyward.validation.getHelpMessage(controlWithoutQualifier, jQContainer);
                    if (targetsWithoutQualifier.length > 0)
                    {
                        resultControlName = controlWithoutQualifier;
                        resultTarget = targetsWithoutQualifier;
                        resultControl = jQContainer.find(`[name="${controlWithoutQualifier}"]`);
                    }
                }
                const isBase = resultControlName.toLowerCase() === "base" || overrideBase;

                return {
                    resultControlName: resultControlName,
                    target: resultTarget,
                    hasTarget: resultTarget.length > 0 && !isBase,
                    control: resultControl,
                    isVisible: resultTarget.is(":visible"),
                    messages: validationCollection[controlName]!,
                    isBase: isBase,
                };
            }

            function getMessageAccumulator(includeInvisible: boolean)
            {
                return function (accumulator: number, currentValue: ValidationItem): number
                {
                    if (includeInvisible || currentValue.isVisible || currentValue.isBase)
                    {
                        return accumulator + currentValue.messages.length;
                    }

                    return accumulator;
                };
            }

            function displayValidationListItems(validationCollection: Dictionary<string, ValidationItem>, errorCollection: Dictionary<string, ValidationItem>, baseUl: JQuery, type: ValidationType)
            {
                const baseUiMessages = new Array();
                const seenBaseMessages = new Set();

                for (const controlName in validationCollection)
                {
                    const item = validationCollection[controlName]!;
                    const isBase = item.isBase;
                    // Reset target to prevent future iterations from appending the wrong validation messages
                    let target = null;

                    if (!isBase)
                    {
                        target = item.target;

                        if (target.length > 0 && item.isVisible === false)
                        {
                            continue;
                        }
                    }

                    const listItems = generateValidationListItems(item.messages, item.resultControlName, jQContainer, type.Severity, type.SkipStyling(errorCollection[controlName]!), overrideBase);

                    if (isBase)
                    {
                        baseUl.append(listItems);
                        baseUiMessages.push(listItems);
                    }
                    else
                    {
                        if (target!.length > 0)
                        {
                            appendValidationMessageListToTarget(target!, listItems, controlName);
                            const dirty = item.control.attr("data-dirty");
                            if (dirty === undefined || dirty === "false")
                            {
                                item.control.attr("data-original-value-invalid", "");
                            }
                        }
                        else if (!seenBaseMessages.has(item.messages.join("\n")))
                        {
                            baseUl.append(listItems);
                            baseUiMessages.push(listItems);
                            seenBaseMessages.add(item.messages.join("\n"));
                        }
                    }
                }
            }

            function appendValidationMessageListToTarget(target: JQuery, validationListItems: HTMLElement[], controlName: string)
            {
                // TODO this will never work
                let targetValidation = validationMessageListsForTargets[controlName as any] as unknown as ValidationMessageListItem["value"];
                if (!targetValidation)
                {
                    const validationList = $("<ul>", { 'class': 'validationList' });
                    validationList.append(validationListItems);
                    targetValidation =
                    {
                        target: target,
                        validationList: validationList,
                    };
                    validationMessageListsForTargets.push({ key: controlName, value: targetValidation });
                    targetValidation.target.append(targetValidation.validationList);
                }
                else
                {
                    targetValidation.validationList.append(validationListItems);
                }
            }
        };

        function mapValues<T extends Object, TOut>(
            target: T,
            mapFn: (element: ValueOf<T>, key: keyof T, obj: T) => TOut):
            { [key in keyof T]: TOut }
        {
            const result = Object.create(Object.getPrototypeOf(target));
            for (const key of Object.keys(target) as (keyof T)[])
            {
                result[key] = mapFn(target[key], key as keyof T, target);
            }
            return result;
        };

        function validYear(year: number)
        {
            // make sure year is above sql min and below skyward max
            return !isNaN(year) && year >= 1 && year <= 9999;
        }

        skyward.validation.validDate = function (date)
        {
            const parsed = skyward.time.parseDate(date, false);
            return validYear(parsed.getFullYear());
        };

        // a data-decimal-mask allows a negative sign and a period. A valid number on the UI side cannot contain either without a number present
        skyward.validation.validNumber = function (n)
        {
            return !isNaN(typeof n === "number" ? n : parseFloat(n)) && isFinite(n as number);
        };

        const generateValidationListItems = function (data: ValidationMessage[], control: string, jQContainer: JQuery, severity: Severity, skipStyling: boolean, overrideBase: boolean)
        {
            const listItems = [];
            const cssSeverity = severity.toLowerCase();

            for (let i = 0; i < data.length; i++)
            {
                const itemContent = data[i];
                let message = itemContent;
                if (typeof itemContent !== "string")
                {
                    message = itemContent.message;
                }
                if (message != '')
                {
                    const listItem = $("<li>",
                        {
                            addClass: cssSeverity + ((control.toLowerCase() == "base") || overrideBase ? "-list" : "-text"),
                            append: $("<span>",
                                {
                                    append:
                                        [
                                            getSvg(severity, severity).addClass("alert" + severity).addClass("svgNoHover"),
                                            $("<span>",
                                                {
                                                    addClass: "validationSpan",
                                                    text: message
                                                })
                                        ]
                                })
                        });
                    listItems.push(listItem[0]);

                    if (!skipStyling && control.toLowerCase() != "base" && !overrideBase)
                    {
                        skyward.validation.getLabel(control, jQContainer).addClass(cssSeverity + "-text");
                        skyward.validation.getControl(control, jQContainer).addClass(cssSeverity + "-control").trigger("errorChange");
                        // only need to do this once per control.
                        skipStyling = true;
                    }

                }
            }
            return listItems;
        };

        skyward.validation.getControl = function (name, container)
        {
            const selector = `[data-validate-control-for="${name}"]`;
            return container ? container.find(selector) : $(selector);
        };

        skyward.validation.getHelpMessage = function (name, container)
        {
            const selector = `[data-validate-message-for="${name}"]`;
            return container ? container.find(selector) : $(selector);
        };

        skyward.validation.getLabel = function (name, container)
        {
            const selector = `[data-validate-text-for="${name}"]`;
            return container ? container.find(selector) : $(selector);
        };

    }(window.skyward = window.skyward || {}, window.jQuery)
);
